import { CartLinesUpdateEvent } from '@shopify/events';

const swatches = {"Black":"#252625","White":"#faf9f5","Charcoal Gray":"#535453","Dark Navy":"#242b3d","Navy Blue":"#364260","Airforce Blue":"#46647a","Hunter Green Camo":"#596346","Desert Sand":"#baa68b","Mushroom":"#aaa28f","Lincoln Green":"#455d51","Khaki":"#b4a583","Dark Brown":"#654c3b","Olive Green":"#6b704b","Silver":"#a6a7a4","Hunter Green":"#345c48","Burgundy":"#6c263a","Diesel Gray":"#777977","Steel Blue":"#567989","Ink Navy":"#202d3c","Dark Olive":"#4d593f","Brown Duck":"#a47c50","Nickel Gray":"#858883","Moss Green":"#626b4b","Military Khaki":"#aca77e","Charcoal":"#535453","Heather Gray":"#bdbdbb","Navy":"#28364d","Medium Stonewash - Medium Wash":"#526f91","Rinse - Dark Wash":"#273c57","Dark Stonewash - Dark Wash":"#273c57","Black - Black":"#252625","Filbert Nights - Black":"#252625","Rockwell Morning - Dark Wash":"#273c57","Optic Daisy - White":"#f7f5eb","Clean Rigid - Dark Wash":"#273c57","Light Stonewash - Light Wash":"#90acc3","New York To LA - Light Wash":"#90acc3","At Cafe Grey - Grey":"#a3a3a0","Brown Times Garment Dye - Brown":"#827269","Forgot Your Stretch - Medium Wash":"#526f91","Weekend Tailgate - Medium Wash":"#526f91","3 pm In Columbus - Dark Wash":"#273c57","Listless - Black":"#252625","At The Museum - Light Wash":"#90acc3","Along The Coast - Medium Wash":"#526f91","DiRienzo - Grey":"#a3a3a0","Rigid - Dark Wash":"#273c57","Black Rigid- Black":"#252625","Silver Rigid - Grey":"#a3a3a0","Modern Black Rigid- Black":"#252625","Green Rigid - Green":"#46634f","Dark Brown Rigid - Brown":"#827269","Beige Rigid - Tan":"#d0c3a8","Evolution Black Rigid - Black":"#252625","Olive":"#69704d","Camo":"#596346","Maroon":"#702b35"};
const colorLabel = (color) => color;
const aggregateItems = (rows) => {
  const quantities = new Map();
  for (const row of rows) quantities.set(row.id, (quantities.get(row.id) || 0) + row.quantity);
  return [...quantities].map(([id, quantity]) => ({ id, quantity }));
};

class ZarCatalogProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.controller = new AbortController();
    this.rows = [];
    this.busy = false;
    this.data = JSON.parse(this.querySelector('[data-zar874-data]').textContent);
    this.copy = this.data.copy;
    this.hasLength = this.data.hasLength;
    this.money = (cents) => new Intl.NumberFormat(this.data.locale, { style: 'currency', currency: this.data.currency }).format(cents / 100);
    this.form = this.querySelector('[data-builder]');
    this.status = this.querySelector('[data-status]');
    this.submit = this.querySelector('[data-submit]');
    this.another = this.querySelector('[data-another]');
    const signal = this.controller.signal;
    this.form.addEventListener('submit', (event) => this.addToBag(event), { signal });
    this.another.addEventListener('click', () => {
      const previous = this.rows[this.rows.length - 1];
      this.addRow({ color: previous?.color || this.colors[0], waist: previous?.waist || '', inseam: previous?.inseam || '', quantity: 1 });
      this.active.element.querySelector('[data-color]').focus();
    }, { signal });
    this.loadChoices();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.initialized = false;
  }

  async loadChoices() {
    try {
      const variants = [...this.data.variants];
      for (let page = 1; page <= this.data.pages; page++) {
        if (page === this.data.page) continue;
        const url = new URL(this.dataset.productUrl, window.location.origin);
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(this.data.pageParam, String(page));
        const response = await fetch(url.toString(), { credentials: 'same-origin', signal: this.controller.signal });
        if (!response.ok) throw new Error('Choices unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = html.querySelector('[data-zar874-data]');
        if (!payload) throw new Error('Choices unavailable');
        variants.push(...JSON.parse(payload.textContent).variants);
      }
      this.variants = [...new Map(variants.map((variant) => [variant.id, variant])).values()]
        ;
      this.colors = this.data.colors.filter((color) => this.variants.some((variant) => variant.color === color));
      if (!this.colors.length) throw new Error('No choices');
      this.buildSwatches();
      const explicitId = new URL(window.location.href).searchParams.get('variant');
      const selected = explicitId ? this.variants.find((variant) => String(variant.id) === explicitId) : undefined;
      this.addRow({ color: selected?.color || this.colors[0], waist: selected?.waist || '', inseam: selected?.inseam || '', quantity: 1 });
      this.another.disabled = false;
      this.submit.disabled = false;
      this.submit.innerHTML = '';
      this.submit.append(document.createTextNode(this.copy.addAll));
      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↗';
      this.submit.append(arrow);
      this.setStatus(this.copy.ready);
    } catch (error) {
      if (error.name !== 'AbortError') this.setStatus(this.copy.loadError, true);
    }
  }

  buildSwatches() {
    const container = this.querySelector('[data-swatches]');
    container.replaceChildren();
    for (const color of this.colors) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zar874-swatch';
      button.dataset.swatchColor = color;
      button.style.setProperty('--swatch', swatches[color] || '#bbb');
      button.setAttribute('aria-pressed', 'false');
      const chip = document.createElement('i');
      chip.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = colorLabel(color);
      button.append(chip, label);
      button.addEventListener('click', () => {
        if (this.busy || !this.active) return;
        this.active.color = color;
        this.active.element.querySelector('[data-color]').value = color;
        this.syncRow(this.active);
        this.activate(this.active);
        this.updateTotals();
      }, { signal: this.controller.signal });
      container.append(button);
    }
  }

  option(select, value, label = value) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    select.append(option);
    return option;
  }

  addRow(values) {
    const element = this.querySelector('[data-pair-template]').content.firstElementChild.cloneNode(true);
    const row = { ...values, element };
    const number = this.rows.length + 1;
    for (const key of ['color', 'waist', 'inseam', 'quantity']) {
      const control = element.querySelector('[data-' + key + ']');
      control.id = this.dataset.sectionId + '-pair-' + number + '-' + key + '-' + Date.now();
      const label = key === 'quantity' ? element.querySelector('.zar874-row-quantity label') : control.closest('label');
      label.htmlFor = control.id;
    }
    const colors = element.querySelector('[data-color]');
    this.colors.forEach((color) => this.option(colors, color, colorLabel(color)));
    colors.value = row.color;
    const waist = element.querySelector('[data-waist]');
    this.data.sizes.forEach((value) => this.option(waist, value));
    waist.value = row.waist;
    const inseam = element.querySelector('[data-inseam]');
    this.data.lengths.forEach((value) => this.option(inseam, value));
    inseam.value = row.inseam;
    inseam.closest('label').hidden = !this.hasLength;
    inseam.required = this.hasLength;
    element.querySelector('[data-quantity]').value = String(row.quantity);
    for (const button of element.querySelectorAll('[data-quantity-minus],[data-quantity-plus]')) button.dataset.quantityLabel = button.getAttribute('aria-label');
    element.addEventListener('focusin', () => this.activate(row), { signal: this.controller.signal });
    element.addEventListener('change', () => {
      if (this.busy) return;
      row.color = colors.value;
      row.waist = waist.value;
      row.inseam = inseam.value;
      row.quantity = Number(element.querySelector('[data-quantity]').value);
      this.syncRow(row);
      this.activate(row);
      this.updateTotals();
    }, { signal: this.controller.signal });
    element.querySelector('[data-quantity]').addEventListener('input', () => {
      row.quantity = Number(element.querySelector('[data-quantity]').value);
      this.updateTotals();
    }, { signal: this.controller.signal });
    for (const [selector, delta] of [['[data-quantity-minus]', -1], ['[data-quantity-plus]', 1]]) {
      element.querySelector(selector).addEventListener('click', () => {
        if (this.busy) return;
        const input = element.querySelector('[data-quantity]');
        const current = Number(input.value);
        row.quantity = Number.isInteger(current) && current >= 1 && current <= 999 ? Math.min(999, Math.max(1, current + delta)) : 1;
        input.value = String(row.quantity);
        this.activate(row);
        this.updateTotals();
      }, { signal: this.controller.signal });
    }
    element.querySelector('[data-remove]').addEventListener('click', () => {
      if (this.busy || this.rows.length === 1) return;
      const index = this.rows.indexOf(row);
      this.rows.splice(index, 1);
      element.remove();
      this.activate(this.rows[Math.min(index, this.rows.length - 1)]);
      this.updateTotals();
      this.active.element.querySelector('[data-color]').focus();
    }, { signal: this.controller.signal });
    this.rows.push(row);
    this.querySelector('[data-pairs]').append(element);
    this.syncRow(row);
    this.activate(row);
    this.updateTotals();
  }

  syncRow(row) {
    const matching = this.variants.filter((variant) => variant.color === row.color);
    const waistControl = row.element.querySelector('[data-waist]');
    for (const option of waistControl.options) {
      if (option.value) option.disabled = !matching.some((variant) => variant.waist === option.value && variant.available);
    }
    if (row.waist && !matching.some((variant) => variant.waist === row.waist && variant.available)) {
      row.waist = '';
      waistControl.value = '';
    }
    const lengthControl = row.element.querySelector('[data-inseam]');
    for (const option of lengthControl.options) {
      if (option.value) option.disabled = !matching.some((variant) => variant.inseam === option.value && (!row.waist || variant.waist === row.waist) && variant.available);
    }
    if (row.inseam && !matching.some((variant) => variant.inseam === row.inseam && (!row.waist || variant.waist === row.waist) && variant.available)) {
      row.inseam = '';
      lengthControl.value = '';
    }
    row.variant = matching.find((variant) => variant.waist === row.waist && variant.inseam === row.inseam);
    const error = row.element.querySelector('[data-row-error]');
    error.hidden = !row.waist || !row.inseam || Boolean(row.variant?.available);
    error.textContent = this.copy.unavailable;
  }

  activate(row) {
    if (!row) return;
    this.active = row;
    for (const item of this.rows) item.element.classList.toggle('is-active', item === row);
    for (const button of this.querySelectorAll('[data-swatch-color]')) button.setAttribute('aria-pressed', String(button.dataset.swatchColor === row.color));
    this.querySelector('[data-selected-color]').textContent = colorLabel(row.color);
    this.querySelector('[data-photo-color]').textContent = colorLabel(row.color);
    const priceVariant = row.variant || this.variants.find((variant) => variant.color === row.color && variant.available);
    if (priceVariant) {
      this.querySelector('[data-main-price]').textContent = this.money(priceVariant.price);
      const compare = this.querySelector('[data-main-compare]');
      compare.hidden = !(priceVariant.compareAtPrice > priceVariant.price);
      compare.textContent = compare.hidden ? '' : this.money(priceVariant.compareAtPrice);
    }
    if (this.galleryColor !== row.color) this.updateGallery(row.color);
    if (row.variant?.available) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(row.variant.id));
      window.history.replaceState({}, '', url.toString());
    }
  }

  updateGallery(color) {
    this.galleryColor = color;
    let images = this.data.images.filter((image) => this.data.galleryMode === 'all_color_views' ? (image.alt.startsWith(color + ' ') || image.alt === 'Size chart') : ['Front', 'Back', 'Left Side', 'Right Side'].some((view) => image.alt === color + ' ' + view));
    const main = this.querySelector('.zar874-main-image');
    main.hidden = !images.length;
    this.querySelector('[data-photo-missing]').hidden = Boolean(images.length);
    const container = this.querySelector('[data-thumbnails]');
    container.replaceChildren();
    const show = (image, button) => {
      main.removeAttribute('srcset');
      main.src = image.url;
      main.alt = image.alt;
      container.querySelectorAll('button').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    };
    images.forEach((image, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', this.copy.view + ' ' + image.alt);
      button.setAttribute('aria-pressed', String(index === 0));
      const thumbnail = document.createElement('img');
      thumbnail.src = image.thumb;
      thumbnail.alt = '';
      thumbnail.loading = 'lazy';
      button.append(thumbnail);
      button.addEventListener('click', () => show(image, button), { signal: this.controller.signal });
      container.append(button);
      if (index === 0) show(image, button);
    });
  }

  updateTotals() {
    let total = 0;
    let quantity = 0;
    this.rows.forEach((row, index) => {
      const validQuantity = Number.isInteger(row.quantity) && row.quantity > 0 && row.quantity <= 999;
      const lineTotal = row.variant?.available && validQuantity ? row.variant.price * row.quantity : 0;
      total += lineTotal;
      quantity += validQuantity ? row.quantity : 0;
      row.element.querySelector('[data-line-price]').textContent = row.variant?.available && validQuantity ? this.money(lineTotal) : '';
      row.element.querySelector('[data-pair-label]').textContent = this.copy.pair + ' ' + (index + 1);
      const minus = row.element.querySelector('[data-quantity-minus]');
      const plus = row.element.querySelector('[data-quantity-plus]');
      minus.disabled = this.busy || (validQuantity && row.quantity <= 1);
      plus.disabled = this.busy || (validQuantity && row.quantity >= 999);
      for (const button of [minus, plus]) {
        button.setAttribute('aria-label', button.dataset.quantityLabel + ' ' + this.copy.pair + ' ' + (index + 1));
      }
      const remove = row.element.querySelector('[data-remove]');
      remove.disabled = this.rows.length === 1 || this.busy;
      remove.setAttribute('aria-label', this.copy.remove + ' ' + this.copy.pair + ' ' + (index + 1));
    });
    this.querySelector('[data-total]').textContent = this.money(total);
    this.querySelector('[data-pair-count]').textContent = (quantity === 1 ? this.copy.countOne : this.copy.count).replace('[count]', String(quantity));
  }

  setStatus(text, error = false) {
    this.status.textContent = text;
    this.status.classList.toggle('is-error', error);
    this.status.setAttribute('role', error ? 'alert' : 'status');
  }

  async addToBag(event) {
    event.preventDefault();
    if (this.busy || !this.form.reportValidity()) return;
    const invalid = this.rows.find((row) => !row.variant?.available);
    if (invalid) {
      this.setStatus(this.copy.unavailable, true);
      invalid.element.querySelector('[data-waist]').focus();
      return;
    }
    const items = aggregateItems(this.rows.map((row) => ({ id: row.variant.id, quantity: row.quantity })));
    const pairCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.busy = true;
    this.form.querySelectorAll('button,select,input').forEach((control) => { control.disabled = true; });
    this.querySelectorAll('[data-swatch-color]').forEach((control) => { control.disabled = true; });
    this.submit.textContent = this.copy.adding;
    this.querySelector('[data-view-bag]').hidden = true;
    this.setStatus(this.copy.adding);
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise
    }));
    try {
      const response = await fetch(this.dataset.root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || this.copy.error);
      const cartResponse = await fetch(this.dataset.root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.copy.error);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: added.sections, source: 'zar-874-builder', sourceId: this.dataset.sectionId, itemCount: pairCount, productId: this.dataset.productId, didError: false }
      });
      this.setStatus(this.copy.added.replace('[count]', String(pairCount)));
      this.querySelector('[data-view-bag]').hidden = false;
    } catch (error) {
      deferred.reject(error);
      this.setStatus(this.copy.error, true);
      this.querySelector('[data-view-bag]').hidden = false;
    } finally {
      this.busy = false;
      this.form.querySelectorAll('button,select,input').forEach((control) => { control.disabled = false; });
      this.querySelectorAll('[data-swatch-color]').forEach((control) => { control.disabled = false; });
      this.rows.forEach((row) => this.syncRow(row));
      this.updateTotals();
      this.submit.textContent = this.copy.addAll;
    }
  }
}

if (!customElements.get('zar-catalog-product')) customElements.define('zar-catalog-product', ZarCatalogProduct);
